import { useCallback, useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { useSelector, useDispatch } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import _get from 'lodash/get';

import {
  GET_INITIAL_DATA,
  GET_PART_ADJUSTMENTS,
  GET_JOB_QUANTITY,
  GET_RECENT_OPERATOR_RUNS,
  GET_CURRENT_EXPECTED_SHIFT_BOUNDS,
  GET_OPERATORS,
  GET_ACTIVITY_TYPES,
  GET_DOWNTIME,
  GET_WORKFLOWS,
  GET_PART_BUCKETS_PARTITIONED_BY_SCHEDULED,
  GET_LABS_JWT,
  GET_AGENT_STATUS,
  GET_MACHINE_GROUPS,
  GET_MACHINES,
  GET_SELECTED_WORK_ORDER_OPERATION,
  GET_ERP_RESOURCES,
  GET_WORK_ORDER_OPERATION_PARTS_GQL,
  GET_WORK_ORDER_OPERATION_PARTS,
  GET_OEE,
  GET_ANNOTATION_TYPES,
} from 'lib/api/queries';
import { useNetworkError, useRequestRecentActivitySets } from 'lib/hooks';
import {
  POLL_INTERVAL,
  ERROR_KEYS,
  LIGHT_UPDATE_INTERVAL,
  THIRTY_SECOND_INTERVAL,
  LAB_REFRESH_DURATION,
  SIXTY_SECOND_INTERVAL,
} from 'lib/constants';
import { getMachine } from 'lib/selectors/getMachine';
import { getScopeStart } from 'lib/selectors/getScopeStart';
import { getScopeEnd } from 'lib/selectors/getScopeEnd';
import { getLatestActivitySet } from 'lib/selectors/getLatestActivitySet';
import {
  actionSetPartAdjustments,
  actionSetInitialData,
  actionSetShiftStatusIntervals,
  actionSetLoading,
  actionUnsetLoading,
  actionSetJobTotal,
  actionSetRecentOperatorRuns,
  actionSetShifts,
  actionSetOperators,
  actionSetActivityTypes,
  actionSetLastNonIdle,
  actionSetDowntimeIntervals,
  actionSetPartCountBuckets,
  actionSetWorkflows,
  actionSetLabsAuthorized,
  actionSetUtilizationHourlies,
  actionSetAgentStatus,
  actionSetMachineGroups,
  actionSetMachines,
  actionSetCurrentWorkOrderOp,
  actionSetLoadingCurrentWorkOrderOp,
  actionSetMachineERPResources,
  actionSetWorkOrderOperationLaborTickets,
  actionSetWorkOrderOperationParts,
  actionSetLaunchDarkleyFlags,
  actionSetOEE,
  actionSetAnnotationTypes,
} from 'lib/actions';
import { getIsLoadingJobRunASWrapper } from 'lib/selectors/getIsLoadingJobRunASWrapper';
import { getIsInitialDataSet } from 'lib/selectors/getIsInitialDataSet';
import { getIsInitialShiftPollLoading } from 'lib/selectors/getIsInitialShiftPollLoading';
import { checkIsLatestShiftActive } from 'lib/selectors/checkIsLatestShiftActive';
import { getStartOfCurrentShopDay } from 'lib/selectors/getStartOfCurrentShopDay';
import {
  formatIntervalsAsTSRange,
  startOfAppHistory,
  takeEarliest,
} from 'lib/utils/date';
import { getStartOfSixDaysPrior } from 'lib/selectors/getStartOfSixDaysPrior';
import { getPartKeys } from 'lib/selectors/getPartKeys';
import { getLastPartTimestamp } from 'lib/selectors/getLastPartTimestamp';
import { getLatestJobQuantity } from 'lib/selectors/getLatestJobQuantity';
import { getPartCountQueryIntervals } from 'lib/selectors/getPartCountQueryIntervals';
import { getIsOperatorTrackingEnabled } from 'lib/selectors/getIsOperatorTrackingEnabled';
import { getSetupModeStart } from 'lib/selectors/getSetupModeStart';
import { getShiftStart } from 'lib/selectors/getShiftStart';
import {
  getHasAHistoricalPartsTab,
  getHasDowntimeTab,
  getHasPartsGoalTab,
  getHasLabTab,
} from 'lib/selectors/getTabs';
import { LABS_URL } from 'lib/appConfig';
import { bugsnag } from 'lib/external/bugsnag';
import { utilizationSlices } from 'components/charts/utilizationSlices';
import { getGroupIdsForMachine } from 'lib/selectors/getMachineGroups';
import { useIsWorkOrderFlow } from 'lib/hooks/useIsWorkOrderFlow';
import { getHasOpenActivitySet } from 'lib/selectors/getHasOpenActivitySet';
import { getCurrentWorkOrderOp } from 'lib/selectors/getCurrentWorkOrderOp';
import { getWorkOrderOpActionLoading } from 'lib/selectors/getWorkOrderOpActionLoading';
import { getLaborTicketActionLoading } from 'lib/selectors/getLaborTicketActionLoading';
import { getLoadingCurrentWorkOrderOp } from 'lib/selectors/getLoadingCurrentWorkOrderOp';
import { getHasOpenActivity } from 'lib/selectors/getHasOpenActivity';
import { getIsLaborTicketEnabled } from 'lib/selectors/getIsLaborTicketEnabled';
import { getWorkOrderFlowEnabled } from 'lib/selectors/getWorkOrderFlowEnabled';
import { getPostLaborUpdatesOnNats } from 'lib/selectors/getPostLaborUpdatesOnNats';
import { useRequestMachineSchedule } from 'lib/hooks/useRequestMachineSchedule';
import { getProductionScheduleEnabled } from 'lib/selectors/getProductionScheduleEnabled';
import { useMachineOperationalStatus } from 'lib/hooks/useMachineOperationalStatus';
import { getFirstUserAddedManualPartCountsPartTime } from 'lib/selectors/getFirstUserAddedManualPartCountsPartTime';

const useInitialData = () => {
  const machine = useSelector(getMachine);
  const scopeStart = useSelector(getScopeStart);
  const firstUserAddedManualPartCountsPartTime = useSelector(
    getFirstUserAddedManualPartCountsPartTime
  );
  const scopeEnd = useSelector(getScopeEnd);
  const shiftStart = useSelector(getShiftStart);
  const latestActivitySet = useSelector(getLatestActivitySet);
  const latestJobQuantity = useSelector(getLatestJobQuantity);
  const { jobId } = latestActivitySet;
  const dispatch = useDispatch();
  const runLoading = useSelector(getIsLoadingJobRunASWrapper);
  const isInitialDataSet = useSelector(getIsInitialDataSet);
  const partKeys = useSelector(getPartKeys);
  const lastPartTimestamp = useSelector(getLastPartTimestamp);
  const sixDaysPrior = useSelector(getStartOfSixDaysPrior);
  const partCountQueryIntervals = useSelector(getPartCountQueryIntervals);
  const startOfHistory = startOfAppHistory();
  const setupModeStart = useSelector(getSetupModeStart);
  const isOperatorTrackingEnabled = useSelector(getIsOperatorTrackingEnabled);
  const hasPartsGoalTab = useSelector(getHasPartsGoalTab);
  const hasDowntimeTab = useSelector(getHasDowntimeTab);
  const hasAHistoricalPartsTab = useSelector(getHasAHistoricalPartsTab);
  const hasLabTab = useSelector(getHasLabTab);
  const isInitialShiftPollLoading = useSelector(getIsInitialShiftPollLoading);
  const isLatestShiftActive = useSelector(checkIsLatestShiftActive);
  const startOfCurrentShopDay = useSelector(getStartOfCurrentShopDay);
  const loadingCurrentWorkOrderOp = useSelector(getLoadingCurrentWorkOrderOp);
  const hasOpenActivity = useSelector(getHasOpenActivity);

  const flags = useFlags();

  // there are times we _need_ access to flags in our selectors. Placing them in redux enabled this for us
  useEffect(() => {
    dispatch(actionSetLaunchDarkleyFlags(flags));
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [flags]);

  const laborTicketEnabled = useSelector(getIsLaborTicketEnabled);
  const workOrderFlowEnabled = useSelector(getWorkOrderFlowEnabled);
  const showWorkOrderFlow = useIsWorkOrderFlow();
  const currentWorkOrderOp = useSelector(getCurrentWorkOrderOp);
  const productionScheduleEnabled = useSelector(getProductionScheduleEnabled);
  const fetchMachineOperationalStatus = useMachineOperationalStatus();
  const natsFlowEnabled = useSelector(getPostLaborUpdatesOnNats);

  useEffect(() => {
    if (!currentWorkOrderOp && workOrderFlowEnabled) {
      dispatch(actionSetCurrentWorkOrderOp(null));
      dispatch(actionSetWorkOrderOperationLaborTickets(null));
      dispatch(actionSetWorkOrderOperationParts(null));
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [workOrderFlowEnabled, JSON.stringify(currentWorkOrderOp)]);

  const { startOfSixDaysPrior, historyStart } = useMemo(() => {
    return {
      startOfSixDaysPrior: sixDaysPrior,
      historyStart: startOfHistory,
    };
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [startOfCurrentShopDay, shiftStart, machine.machineRef]);

  // Activity Types
  const requestActivityTypes = useQuery(GET_ACTIVITY_TYPES, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      dispatch(actionSetActivityTypes(data.activityTypes));
    },
  });

  const { refetch: refetchActivityTypes } = requestActivityTypes;
  useNetworkError(requestActivityTypes, ERROR_KEYS.ACTIVITY_TYPES, {
    pollInterval: LIGHT_UPDATE_INTERVAL,
  });

  // Annotation Types
  const requestAnnotationTypes = useQuery(GET_ANNOTATION_TYPES, {
    fetchPolicy: 'no-cache',
    variables: {
      machineId: machine.id,
    },
    skip: !machine.id,
    onCompleted: ({ AnnotationType }) => {
      dispatch(actionSetAnnotationTypes(AnnotationType));
    },
  });

  const { refetch: refetchAnnotationTypes } = requestAnnotationTypes;
  useNetworkError(requestAnnotationTypes, ERROR_KEYS.ANNOTATION, {
    pollInterval: SIXTY_SECOND_INTERVAL,
  });

  // LOAD FOR MACHINE SCHEDULE  - IF productionScheduleEnabled
  const skipRequestMachineSchedule = !productionScheduleEnabled || !machine?.id;
  const requestMachineSchedule = useRequestMachineSchedule({
    variables: {
      machineId: machine?.id,
    },
    skip: skipRequestMachineSchedule,
  });

  const { refetch: refetchMachineSchedule } = requestMachineSchedule;
  useNetworkError(requestMachineSchedule, ERROR_KEYS.MACHINE_SCHEDULE);

  // Recent Activity Sets
  const requestRecentActivitySets = useRequestRecentActivitySets();
  const { refetch: refetchRecentActivitySets } = requestRecentActivitySets;
  useNetworkError(requestRecentActivitySets, ERROR_KEYS.RECENT_ACTIVITY_SETS, {
    pollInterval: POLL_INTERVAL,
  });

  // recent operator runs
  const requestRecentOperatorRuns = useQuery(GET_RECENT_OPERATOR_RUNS, {
    variables: {
      machineRef: machine.machineRef,
      limit: 1, // just fetch latest for now
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    skip: !machine.machineRef || !isOperatorTrackingEnabled,
    onCompleted: ({ operatorRuns }) => {
      if (operatorRuns) {
        dispatch(actionSetRecentOperatorRuns(operatorRuns));
      }
    },
  });

  const { refetch: refetchRecentOperatorRuns } = requestRecentOperatorRuns;
  useNetworkError(requestRecentOperatorRuns, ERROR_KEYS.RECENT_OPERATOR_RUNS, {
    pollInterval: THIRTY_SECOND_INTERVAL,
  });

  // OEE
  const requestOEE = useQuery(GET_OEE, {
    variables: {
      machineId: machine?.id,
      startDate: encodeURIComponent(scopeStart),
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    skip: !scopeStart || !machine.id || !hasOpenActivity,
    onCompleted: ({ OEE: oee }) => {
      if (oee) {
        dispatch(actionSetOEE(oee));
      }
    },
  });

  const { refetch: refetchOEE } = requestOEE;
  useNetworkError(requestOEE, ERROR_KEYS.OEE, {
    pollInterval: THIRTY_SECOND_INTERVAL,
  });

  const operatorsQuery = useQuery(GET_OPERATORS, {
    fetchPolicy: 'no-cache',
    skip: !isOperatorTrackingEnabled,
    onCompleted: ({ Operator: operators }) => {
      dispatch(actionSetOperators(operators));
    },
  });

  const { refetch: refetchOperators } = operatorsQuery;
  useNetworkError(operatorsQuery, ERROR_KEYS.OPERATORS, {
    pollInterval: LIGHT_UPDATE_INTERVAL,
  });

  const requestCurrentExpectedShift = useQuery(
    GET_CURRENT_EXPECTED_SHIFT_BOUNDS,
    {
      variables: {
        machineRef: machine.machineRef,
      },
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      skip: !machine.machineRef,
      onCompleted: (res) => {
        dispatch(actionSetShifts({ shifts: [res.shift] }));
        fetchMachineOperationalStatus();
      },
    }
  );

  const machineGroupIds = useSelector(getGroupIdsForMachine);

  const erpResourcesQuery = useQuery(GET_ERP_RESOURCES, {
    variables: {
      machineRef: machine.machineRef,
      machineGroups: machineGroupIds,
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    skip:
      !machine.machineRef ||
      !machineGroupIds ||
      (!laborTicketEnabled && !workOrderFlowEnabled),
    onCompleted: (data) => {
      const erpResources = data?.erpResources;
      if (erpResources) {
        const machineErpResource = erpResources.find((resource) => {
          return resource.machineRef === machine.machineRef;
        });

        const associatedResources = erpResources.map((resource) => {
          return resource.resourceId;
        });

        dispatch(
          actionSetMachineERPResources(
            associatedResources,
            machineErpResource?.resourceId || null
          )
        );
      }
    },
  });

  useNetworkError(erpResourcesQuery, ERROR_KEYS.ERP_RESOURCE);

  const hasOpenActivitySet = useSelector(getHasOpenActivitySet);
  const workOrderOpActionLoading = useSelector(getWorkOrderOpActionLoading);
  const laborTicketActionLoading = useSelector(getLaborTicketActionLoading);
  const workOrderOperationRef =
    hasOpenActivitySet && _get(latestActivitySet, 'workOrderOperationRef');

  // request total parts across all activity sets associated to work order op ref
  const requestWorkOrderOperationPartsLabor = useQuery(
    GET_WORK_ORDER_OPERATION_PARTS_GQL,
    {
      variables: {
        workOrderOperationRef,
      },
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      skip:
        !workOrderOperationRef ||
        !workOrderFlowEnabled ||
        !laborTicketEnabled ||
        loadingCurrentWorkOrderOp ||
        workOrderOpActionLoading ||
        laborTicketActionLoading,
      onCompleted: ({ erpLaborTickets }) => {
        dispatch(actionSetWorkOrderOperationLaborTickets(erpLaborTickets));
      },
    }
  );

  const {
    refetch: refetchWorkOrderOperationPartsLabor,
  } = requestWorkOrderOperationPartsLabor;
  useNetworkError(
    requestWorkOrderOperationPartsLabor,
    ERROR_KEYS.WORK_ORDER_OPERATION_PARTS,
    {
      pollInterval: POLL_INTERVAL,
    }
  );

  // request total parts across all activity sets associated to work order op ref
  const requestWorkOrderOperationParts = useQuery(
    GET_WORK_ORDER_OPERATION_PARTS,
    {
      variables: {
        workOrderOperationRef,
      },
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      skip:
        !workOrderOperationRef ||
        !workOrderFlowEnabled ||
        laborTicketEnabled ||
        workOrderOpActionLoading ||
        laborTicketActionLoading,
      onCompleted: ({ workOrderOperationParts }) => {
        dispatch(actionSetWorkOrderOperationParts(workOrderOperationParts));
      },
    }
  );

  const {
    refetch: refetchWorkOrderOperationParts,
  } = requestWorkOrderOperationParts;
  useNetworkError(
    requestWorkOrderOperationParts,
    ERROR_KEYS.WORK_ORDER_OPERATION_PARTS,
    {
      pollInterval: POLL_INTERVAL,
    }
  );

  const selectedWorkOrderOpQuery = useQuery(GET_SELECTED_WORK_ORDER_OPERATION, {
    variables: {
      workOrderOperationRef,
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    skip:
      !workOrderOperationRef ||
      (!showWorkOrderFlow && !laborTicketEnabled) ||
      natsFlowEnabled,
    onCompleted: (data) => {
      if (data.erpWorkOrderOperation) {
        const getQuantityPerPart = (operation = {}) => {
          const { quantityPerPart = 1 } = operation;
          if (!quantityPerPart || quantityPerPart <= 0) {
            return 1;
          }
          return quantityPerPart;
        };
        const { erpWorkOrderOperation } = data;
        const partOperation = erpWorkOrderOperation.matchingPartOperations[0];
        const partOperationCycleTimeMs = partOperation?.cycleTimeMs || 0;
        const quantityPerPart = getQuantityPerPart(partOperation);

        const workOrderOperation = {
          ...erpWorkOrderOperation,
          quantityPerPart,
          partOperation,
          partOperationRef: partOperation?.partOperationRef,
          // if no operation cycleTimeMs, use cycleTimeMs on partOperation
          cycleTimeMs:
            erpWorkOrderOperation.cycleTimeMs || partOperationCycleTimeMs,
        };
        dispatch(actionSetCurrentWorkOrderOp(workOrderOperation));
      }
    },
  });

  // Reset loading state when workOrderOperationRef changes to handle cases where
  // Apollo starts a new query but our loading state isn't reset
  useEffect(() => {
    // In NATS mode, currentWorkOrderOp comes from labor ticket subscription, not activity set
    if (natsFlowEnabled) {
      dispatch(actionSetLoadingCurrentWorkOrderOp(false));
      return;
    }

    if (workOrderOperationRef) {
      // We have a workOrderOperationRef, so we're about to load a work order operation
      dispatch(actionSetLoadingCurrentWorkOrderOp(true));
    } else {
      // No workOrderOperationRef means no work order operation to load
      dispatch(actionSetCurrentWorkOrderOp(null));
      dispatch(actionSetLoadingCurrentWorkOrderOp(false));
    }
  }, [workOrderOperationRef, natsFlowEnabled]);

  useNetworkError(selectedWorkOrderOpQuery, ERROR_KEYS.CURRENT_WORK_ORDER);

  const { refetch: refetchCurrentExpectedShift } = requestCurrentExpectedShift;

  useNetworkError(
    requestCurrentExpectedShift,
    ERROR_KEYS.CURRENT_EXPECTED_SHIFT,
    {
      pollInterval: THIRTY_SECOND_INTERVAL,
    }
  );

  // Agent Status
  const agentStatus = useQuery(GET_AGENT_STATUS, {
    variables: {
      agentId: machine.agentId,
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    skip: !machine.agentId,
    onCompleted: (data) => {
      return dispatch(actionSetAgentStatus(data.agentStatus));
    },
  });
  const { refetch: refetchAgentStatus } = agentStatus;
  useNetworkError(agentStatus, ERROR_KEYS.AGENT_STATUS, {
    pollInterval: THIRTY_SECOND_INTERVAL,
  });

  // Part Adjustments
  const requestPartAdjustments = useQuery(GET_PART_ADJUSTMENTS, {
    variables: {
      machineId: machine.id,
      start: takeEarliest(scopeStart, setupModeStart),
      end: scopeEnd,
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    skip: !scopeStart || !scopeEnd || !machine.id || !hasAHistoricalPartsTab,
    onCompleted: ({ getPartAdjustments }) => {
      return dispatch(actionSetPartAdjustments(getPartAdjustments));
    },
  });

  const { refetch: refetchPartAdjustments } = requestPartAdjustments;
  useNetworkError(requestPartAdjustments, ERROR_KEYS.PART_ADJUSTMENTS, {
    pollInterval: THIRTY_SECOND_INTERVAL,
  });

  // Part Buckets
  const requestPartBuckets = useQuery(
    GET_PART_BUCKETS_PARTITIONED_BY_SCHEDULED,
    {
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      variables: {
        machineRef: machine.machineRef,
        startDate: scopeStart,
        manualPartStartAt: firstUserAddedManualPartCountsPartTime || scopeStart,
        partKeys,
        lastPartTimestamp,
        bucketWidth: hasPartsGoalTab ? '10m' : '60m',
        scheduledIntervals: formatIntervalsAsTSRange(
          partCountQueryIntervals?.scopeScheduledActivityIntervals
        ),
        unscheduledIntervals: formatIntervalsAsTSRange(
          partCountQueryIntervals?.scopeUnscheduledActivityIntervals
        ),
      },
      skip: !isInitialDataSet || !scopeStart || !machine.id || !partKeys,
      onCompleted: ({
        scheduledParts: scheduledPartBuckets,
        unscheduledParts: unscheduledPartBuckets,
        manualParts: manualPartBuckets,
      }) => {
        dispatch(
          actionSetPartCountBuckets({
            manualPartBuckets,
            scheduledPartBuckets,
            unscheduledPartBuckets,
            // track args used for last res so selectors can ignore stale data
            startDate: scopeStart,
          })
        );
      },
    }
  );

  useNetworkError(requestPartBuckets, ERROR_KEYS.PART_BUCKETS);

  // Job Quantity
  const requestJobTotal = useQuery(GET_JOB_QUANTITY, {
    variables: { jobId },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    skip: !latestJobQuantity,
    onCompleted: ({ jobQuantity }) => {
      dispatch(actionSetJobTotal(jobQuantity.quantity));
    },
  });
  useNetworkError(requestJobTotal, ERROR_KEYS.JOB_QUANTITY, {
    pollInterval: SIXTY_SECOND_INTERVAL,
  });

  // Initial Data
  const requestInitialData = useQuery(GET_INITIAL_DATA, {
    variables: {
      machineRef: machine.machineRef,
      startDate: isLatestShiftActive ? shiftStart : startOfCurrentShopDay,
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    skip:
      isInitialShiftPollLoading ||
      !startOfCurrentShopDay ||
      !machine.id ||
      runLoading ||
      isInitialDataSet,
    onCompleted: ({ executionIntervals, ...data }) => {
      dispatch(actionSetInitialData(data));
      dispatch(actionSetShiftStatusIntervals(executionIntervals));
      dispatch(
        actionSetUtilizationHourlies(
          utilizationSlices(scopeStart, scopeEnd, executionIntervals)
        )
      );
    },
  });

  useNetworkError(requestInitialData, ERROR_KEYS.INITIAL);

  const localDowntimeQueryWindowStart = useMemo(() => {
    if (isLatestShiftActive) {
      return shiftStart;
    }
    return startOfCurrentShopDay;
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [shiftStart, startOfCurrentShopDay, isLatestShiftActive]);

  // Downtime
  const downtimeQuery = useQuery(GET_DOWNTIME, {
    fetchPolicy: 'no-cache',
    variables: {
      machineRef: machine.machineRef,
      windowStart: hasDowntimeTab
        ? historyStart
        : localDowntimeQueryWindowStart,
      nonIdleWindowStart: startOfSixDaysPrior,
      // query has max 7 day limit
    },
    skip:
      isInitialShiftPollLoading ||
      !machine.machineRef ||
      !localDowntimeQueryWindowStart ||
      !historyStart,
    onCompleted: ({ downtimes, lastNonIdleInterval }) => {
      const lastNonIdle = lastNonIdleInterval[0] || null;
      dispatch(actionSetLastNonIdle(lastNonIdle));
      dispatch(actionSetDowntimeIntervals(downtimes));
    },
  });

  useNetworkError(downtimeQuery, ERROR_KEYS.DOWNTIME);

  const requestWorkflows = useQuery(GET_WORKFLOWS, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      dispatch(actionSetWorkflows(data));
    },
  });

  const { refetch: refetchWorkflows } = requestWorkflows;
  useNetworkError(requestWorkflows, ERROR_KEYS.WORKFLOWS, {
    pollInterval: LIGHT_UPDATE_INTERVAL,
  });

  const requestMachineGroups = useQuery(GET_MACHINE_GROUPS, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    onCompleted: ({ data }) => {
      dispatch(actionSetMachineGroups(data));
    },
  });

  const { refetch: refetchMachineGroups } = requestMachineGroups;
  useNetworkError(requestMachineGroups, ERROR_KEYS.MACHINE_GROUPS, {
    pollInterval: LIGHT_UPDATE_INTERVAL,
  });

  useQuery(GET_MACHINES, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    onCompleted: ({ data }) => {
      dispatch(actionSetMachines(data));
    },
  });

  const requestLabsJwt = useQuery(GET_LABS_JWT, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    skip: !LABS_URL || !hasLabTab,
    onCompleted: ({ token }) => {
      return fetch(`${LABS_URL}/auth`, {
        credentials: 'include',
        headers: {
          authorization: `Bearer ${token.token}`,
        },
      })
        .then((resp) => {
          dispatch(actionSetLabsAuthorized(resp.status === 200));
          return null;
        })
        .catch((e) => {
          bugsnag.notify(e);
        });
    },
  });
  const { refetch: refetchLabsJwt } = requestLabsJwt;
  useNetworkError(requestLabsJwt, ERROR_KEYS.LABS_JWT, {
    pollInterval: LAB_REFRESH_DURATION,
  });

  const refetch = useCallback(() => {
    dispatch(actionSetLoading());
    return Promise.all([
      refetchRecentActivitySets(),
      refetchOperators(),
      refetchAgentStatus(),
      refetchRecentOperatorRuns(),
      requestCurrentExpectedShift(),
      refetchPartAdjustments(),
      refetchWorkflows(),
      refetchLabsJwt(),
      refetchMachineGroups(),
      refetchOEE(),
      refetchAnnotationTypes(),
    ]).then(() => {
      return dispatch(actionUnsetLoading());
    });
  }, [
    dispatch,
    refetchOperators,
    refetchAgentStatus,
    refetchRecentActivitySets,
    refetchRecentOperatorRuns,
    refetchCurrentExpectedShift,
    refetchWorkOrderOperationParts,
    refetchWorkOrderOperationPartsLabor,
    refetchActivityTypes,
    refetchMachineSchedule,
    refetchPartAdjustments,
    refetchWorkflows,
    refetchLabsJwt,
    refetchMachineGroups,
    refetchOEE,
    refetchAnnotationTypes,
  ]);

  return { refetch };
};

export { useInitialData };
